import { IAuthTokenResultDto } from "../src/taskpane/interfaces/IAuthTokenResultDto";
import { sameAccountId } from "./sameAccountId";

// How long a caller waits for a refresh another caller started before it asks itself (once).
export const REFRESH_WAIT_MS: number = 10000;

/** A refresh in flight (SharedRefreshes.track): ended once done settled; ranOut when its call ran
 * its whole time limit without an answer (its starter marks it). */
export type RefreshInFlight = {
    accountId: string;
    done: Promise<void>;
    ended: boolean;
    ranOut: boolean;
};

/** Waits for a refresh in flight, at most waitMs: what it settled with, or null when it didn't end in
 * time. */
export const waitForRefresh = <T>(done: Promise<T>, waitMs: number = REFRESH_WAIT_MS): Promise<T | null> => {
    let timer: ReturnType<typeof setTimeout>;
    const giveUp: Promise<null> = new Promise<null>(resolve => {
        timer = setTimeout(() => resolve(null), waitMs);
    });
    return Promise.race([done, giveUp]).then(result => {
        clearTimeout(timer);
        return result;
    });
};

/**
 * The token refreshes in flight per kind, each for one account, and who waits for which. AuthModule
 * decides what a refresh does; this, which callers share it.
 */
export class SharedRefreshes<K extends string> {
    // The refresh per kind started last (ended or not), for one account (follow-up 2026-10-20: a
    // caller that needs the same refresh joins it instead of asking again; callers used to check a
    // flag every 2 s, and every getter waited on any refresh, a still-valid token included). It is
    // the one a waiter waits for, as before 2026-10-26, when it was the one refresh held. Once it
    // ended, a caller still needing a token asks at once; unless its call ran out its limit: that
    // one a caller waits for first, as for one that never ended then.
    private latestRefreshes: { [kind: string]: RefreshInFlight } = {};
    // Per kind, the callers waiting meanwhile for any refresh of that kind to end (waitFor). Each
    // leaves when its wait ends, so one that waits through a long hang holds the same all along
    // (review 2026-10-28: subscribing to the refreshes in flight on each wait held more every ten
    // seconds, for as long as the hang lasted).
    private waiting: { [kind: string]: Set<(ended: RefreshInFlight) => void> } = {};

    /** stateNow: the auth state as it is now (AuthModule.getPersistedAuthState). */
    constructor(private readonly stateNow: () => IAuthTokenResultDto | null) {
    }

    /** Drops every refresh in flight (a sign-out): nobody waits on them any more. */
    clear(): void {
        this.latestRefreshes = {};
    }

    /** Settles when the refresh of this kind started last for accountId ends; null when it doesn't run.
     * What the forced Graph refresh waits for, as before 2026-10-26. */
    whenLatestEnds(kind: K, accountId: string): Promise<void> | null {
        const latest: RefreshInFlight = this.latestRefreshes[kind];
        return latest && !latest.ended && sameAccountId(latest.accountId, accountId) ? latest.done : null;
    }

    /** Starts a refresh, held as the one of this kind started last for accountId until it ends. Held
     * before start runs, so a caller reached from inside it joins it instead of starting another; a
     * throw in start rejects. start marks the refresh when its call ran out its time limit without an
     * answer (2026-10-28: a timer of five minutes also marked calls that were answered, later). */
    track<T>(kind: K, accountId: string, start: (markRanOut: () => void) => Promise<T>): { entry: RefreshInFlight; run: Promise<T> } {
        let settle: () => void = null;
        const entry: RefreshInFlight = { accountId, done: new Promise<void>(resolve => { settle = resolve; }), ended: false, ranOut: false };
        this.latestRefreshes[kind] = entry;
        entry.done.then(() => {
            entry.ended = true;
            this.waiting[kind]?.forEach(onEnd => onEnd(entry));
        });
        const run: Promise<T> = new Promise<T>(resolve => resolve(start(() => { entry.ranOut = true; })));
        run.then(settle, settle);
        return { entry, run };
    }

    /** Waits until the refresh waited for ends (none: null) or waitMs has passed. Meanwhile, whenever
     * another refresh of this kind ends, takes the state if it gives the caller what it needs (taken):
     * what it took, or undefined. */
    private waitFor(kind: K, waited: RefreshInFlight | null, waitMs: number, taken: (state: IAuthTokenResultDto) => boolean): Promise<{ state: IAuthTokenResultDto } | undefined> {
        return new Promise<{ state: IAuthTokenResultDto } | undefined>(resolve => {
            const waiting: Set<(ended: RefreshInFlight) => void> = this.waiting[kind] ?? (this.waiting[kind] = new Set());
            const finish = (took?: { state: IAuthTokenResultDto }): void => {
                clearTimeout(timer);
                waiting.delete(onEnd);
                resolve(took);
            };
            const onEnd = (ended: RefreshInFlight): void => {
                const state: IAuthTokenResultDto = this.stateNow();
                if (ended === waited) {
                    finish();
                } else if (taken(state)) {
                    finish({ state });
                }
            };
            const timer: ReturnType<typeof setTimeout> = setTimeout(() => finish(), waitMs);
            waiting.add(onEnd);
        });
    }

    /**
     * A token refresh shared by every caller that needs it (follow-up 2026-10-20). isNeeded is asked
     * of the state as it is now: a refresh that brought what was needed ends the wait, and a caller
     * whose token is still valid never waits. Otherwise as before 2026-10-26 (a8bbdb48a), now that
     * the calls have a time limit, so that no caller does worse than it did then:
     * - A waiter waits for the refresh started last, at most REFRESH_WAIT_MS; then, if a newer one
     *   started, for that one as long; then it asks itself. With no refresh running it asks at once;
     *   one that ran out its limit it waits for in full, as for one that never ended then.
     * - Whenever another refresh ends meanwhile, a caller takes the state if it now holds a token
     *   that works (works), and otherwise keeps waiting as it was: a 12 s refresh that answered held
     *   the waiters of its replacement until 20-22 s (2026-10-26), and taking any state handed out
     *   the expired tokens of a pause another waiter's call started (review 2026-10-28).
     * - The caller that starts a refresh, or asked itself, gets its outcome, as before, unless
     *   another call brings its account a working token first (on a dead connection it would wait
     *   out the limit): not the one it started from, which a clock set back makes look valid, nor
     *   another account's (the fourth review, 2026-10-28). It doesn't replace its own refresh: a
     *   lone slow refresh would cost a second token call, the server force-refreshing again just
     *   when it is slow (audit 2026-10-27).
     * - Signed out, a waiter gets none when its wait ends, as before, and the caller that started a
     *   refresh when that ends: not sooner, as the user may sign in again meanwhile (the fifth
     *   review, 2026-10-28).
     * The token calls' time limit (ApiService) bounds the wait, and a scheduled refresh's call
     * unanswered after 90 s is asked once more (AuthModule.askOnceMore).
     */
    async share<T>(
        kind: K,
        isNeeded: (state: IAuthTokenResultDto) => boolean,
        start: (state: IAuthTokenResultDto, markRanOut: () => void) => Promise<T>,
        current: (state: IAuthTokenResultDto | null) => T,
        works: (state: IAuthTokenResultDto) => boolean = () => true
    ): Promise<T> {
        const taken = (state: IAuthTokenResultDto): boolean => !!state && !isNeeded(state) && works(state);
        // The refresh this caller started, or asked itself after its waits, and the token it started
        // from (current). What it takes instead must also be another call's, for its account.
        let own: { entry: RefreshInFlight; run: Promise<T>; from: T } = null;
        const brought = (state: IAuthTokenResultDto): boolean =>
            taken(state) && sameAccountId(state.account?.accountId, own.entry.accountId) && current(state) !== own.from;
        // As a waiter: the refresh it waited for last, and how many it waited for.
        let joined: RefreshInFlight = null;
        let joins: number = 0;
        for (;;) {
            const state: IAuthTokenResultDto = this.stateNow();
            if (own ? brought(state) : !state || !isNeeded(state)) {
                return current(state);
            }
            if (!own) {
                const accountId: string = state.account?.accountId;
                const latest: RefreshInFlight = this.latestRefreshes[kind];
                const waitable: boolean = !!latest && sameAccountId(latest.accountId, accountId) && (!latest.ended || latest.ranOut);
                if (waitable && latest !== joined && joins < 2) {
                    joined = latest;
                    joins += 1;
                    const took: { state: IAuthTokenResultDto } | undefined = await this.waitFor(kind, latest.ended ? null : latest, REFRESH_WAIT_MS, taken);
                    if (took) {
                        return current(took.state);
                    }
                    continue;
                }
                const from: T = current(state);
                own = { ...this.track(kind, accountId, markRanOut => start(state, markRanOut)), from };
                own.run.catch(() => undefined);
            }
            const took: { state: IAuthTokenResultDto } | undefined = await this.waitFor(kind, own.entry, REFRESH_WAIT_MS, brought);
            if (took) {
                return current(took.state);
            }
            if (own.entry.ended) {
                return own.run;
            }
        }
    }
}
